import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { endSession, getAccessToken, getRefreshToken, getSessionEpoch, saveSessionTokens, subscribeSessionEnd, notifySessionRecovered } from '../../shared/lib/auth/session';

type SessionConfig = AxiosRequestConfig & {
    _authRetry?: boolean; _authTokens?: string[]; _skipAuthRefresh?: boolean; _sessionEpoch?: string;
};
const request = axios.create({
    withCredentials: true,
    baseURL: process.env.REACT_APP_API_BASEURL ?? 'https://api-demo.timetask.ru/api/',
});
export class SessionRecoveryError extends Error {
    constructor() { super('Session recovery pending'); this.name = 'SessionRecoveryError'; }
}
export function isSessionRecoveryError(error: unknown): error is SessionRecoveryError {
    return error instanceof SessionRecoveryError;
}
let pending: {epoch: string; promise: Promise<string>} | undefined;
let identity: {epoch: string; promise: Promise<string>} | undefined;
let recovery: {epoch: string; stop: () => void} | undefined;
function assertSession(epoch: string) { if (epoch !== getSessionEpoch()) throw new axios.Cancel('Session changed'); }
function isAuthEndpoint(url = '') {
    return /(?:^|\/)(?:Account\/(?:Login|Register|refresh|ForgotPassword|ResetPassword)|auth\/[^/]+\/callback)\/?(?:\?.*)?$/i.test(url);
}
function temporary(error: unknown) {
    return axios.isAxiosError(error) && !axios.isCancel(error) &&
        (!error.response || error.response.status >= 500 || [408, 429].includes(error.response.status));
}
const pause = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

// One bounded retry cycle, including time spent waiting for another tab's lock.
async function refreshLocked(epoch: string, failedToken: string | undefined) {
    const deadline = Date.now() + 20000;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 20000);
    const off = subscribeSessionEnd(() => controller.abort());
    const refresh = async () => {
        for (let attempt = 0; attempt < 3; attempt++) {
            assertSession(epoch);
            const current = getAccessToken();
            if (current && current !== failedToken) return current;
            const refreshToken = getRefreshToken();
            if (!refreshToken) { endSession(); throw new Error('Session expired'); }
            if (controller.signal.aborted || Date.now() >= deadline) throw new SessionRecoveryError();
            try {
                const response = await request.post('/Account/refresh', {refreshToken}, {
                    _skipAuthRefresh: true, _sessionEpoch: epoch,
                    timeout: Math.min(8000, deadline - Date.now()), signal: controller.signal,
                } as SessionConfig);
                assertSession(epoch);
                const {token, refreshToken: next, userId} = response.data;
                if (typeof token !== 'string' || !token || typeof next !== 'string' || !next) throw new Error('Invalid refresh response');
                const knownUser = localStorage.getItem('userID');
                if (typeof userId === 'string' && userId && knownUser && knownUser !== userId) {
                    endSession(); throw new axios.Cancel('Session identity changed');
                }
                saveSessionTokens(token, next, typeof userId === 'string' ? userId : undefined);
                return token;
            } catch (error) {
                assertSession(epoch);
                const latest = getAccessToken();
                if (latest && latest !== failedToken) return latest;
                if (axios.isAxiosError(error) && error.response?.status === 401) { endSession(); throw error; }
                if (!temporary(error) && !controller.signal.aborted) throw error;
                if (attempt === 2 || Date.now() + 1000 >= deadline) throw new SessionRecoveryError();
                await pause(attempt === 0 ? 300 : 1000);
            }
        }
        throw new SessionRecoveryError();
    };
    try {
        return navigator.locks?.request
            ? await navigator.locks.request('timetask:refresh', {signal: controller.signal}, refresh)
            : await refresh();
    } catch (error) {
        assertSession(epoch);
        if (controller.signal.aborted) throw new SessionRecoveryError();
        throw error;
    } finally { clearTimeout(timer); off(); }
}

function scheduleRecovery(epoch: string, failedToken: string | undefined) {
    if (recovery?.epoch === epoch) return;
    recovery?.stop();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;
    let running = false;
    const stop = () => {
        stopped = true; if (timer) clearTimeout(timer);
        window.removeEventListener('online', run); window.removeEventListener('focus', run); off();
        if (recovery?.epoch === epoch) recovery = undefined;
    };
    const run = () => {
        if (stopped || running) return;
        if (getSessionEpoch() !== epoch || !getRefreshToken()) { stop(); return; }
        if (timer) clearTimeout(timer);
        if (!navigator.onLine) { timer = setTimeout(run, 5000); return; }
        running = true;
        refreshSession(failedToken, epoch).catch(() => {
            if (!stopped) timer = setTimeout(run, 5000);
        }).finally(() => { running = false; });
    };
    const off = subscribeSessionEnd(stop);
    recovery = {epoch, stop};
    window.addEventListener('online', run); window.addEventListener('focus', run);
    timer = setTimeout(run, 5000);
}
export function refreshSession(failedToken = getAccessToken(), epoch = getSessionEpoch()): Promise<string> {
    if (pending?.epoch === epoch) return pending.promise;
    const promise = refreshLocked(epoch, failedToken);
    const entry = {epoch, promise}; pending = entry;
    promise.then(() => {
        if (pending === entry) pending = undefined;
        if (recovery?.epoch === epoch) { recovery.stop(); notifySessionRecovered(); }
    }, error => {
        if (pending === entry) pending = undefined;
        if (isSessionRecoveryError(error) && getSessionEpoch() === epoch) scheduleRecovery(epoch, failedToken);
    });
    return promise;
}

// The server verifies the access/refresh identity. Never decode an expired JWT for the user ID.
export function ensureCurrentUserId(): Promise<string> {
    const epoch = getSessionEpoch();
    const current = localStorage.getItem('userID');
    if (current) return Promise.resolve(current);
    if (identity?.epoch === epoch) return identity.promise;
    const promise = request.get('/Account/Me').then(response => {
        assertSession(epoch);
        const userId = response.data?.id ?? response.data?.userId;
        if (typeof userId !== 'string' || !userId) throw new Error('Invalid current user response');
        localStorage.setItem('userID', userId); return userId;
    });
    const entry = {epoch, promise}; identity = entry;
    const release = () => { if (identity === entry) identity = undefined; };
    promise.then(release, release);
    return promise;
}
request.interceptors.request.use(async config => {
    const session = config as SessionConfig;
    session._sessionEpoch = session._sessionEpoch ?? getSessionEpoch();
    assertSession(session._sessionEpoch);
    if (!session._skipAuthRefresh && !isAuthEndpoint(config.url)) {
        if (pending?.epoch === session._sessionEpoch) await pending.promise;
        for (const body of [config.params, typeof config.data === 'object' ? config.data : undefined]) {
            if (!body) continue;
            for (const key of ['userId', 'UserId']) {
                if (Object.prototype.hasOwnProperty.call(body, key) && body[key] == null) body[key] = await ensureCurrentUserId();
            }
        }
    }
    assertSession(session._sessionEpoch);
    config.headers = config.headers || {};
    const token = getAccessToken();
    if (token) config.headers.Authorization = 'Bearer ' + token;
    else delete config.headers.Authorization;
    return config;
});
request.interceptors.response.use(response => {
    assertSession((response.config as SessionConfig)._sessionEpoch ?? getSessionEpoch());
    return response;
}, async (error: AxiosError) => {
    const config = error.config as SessionConfig | undefined;
    if (!config || error.response?.status !== 401 || config._skipAuthRefresh || isAuthEndpoint(config.url)) return Promise.reject(error);
    const epoch = config._sessionEpoch ?? getSessionEpoch(); assertSession(epoch);
    const sent = String(config.headers?.Authorization || '').replace(/^Bearer /, '') || undefined;
    const tried = config._authTokens || [];
    if (config._authRetry) {
        const current = getAccessToken();
        if (!current || current === sent || tried.includes(current) || tried.length >= 2) {
            if (current === sent) endSession();
            return Promise.reject(error);
        }
    } else await refreshSession(sent, epoch);
    assertSession(epoch);
    config._authRetry = true;
    config._authTokens = [...tried, sent || ''];
    return request.request(config);
});
export default request;
